/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@mui/material';
import { createPortal } from 'react-dom';
import HighlightableTextOverlay from '../highlightable-text-overlay/HighlightableTextOverlay';
import { SelectionReference } from '../highlightable-text-overlay/HighlightableTextOverlay.props';
import HighlightedTextMarker from '../highlighted-text-marker/HighlightedTextMarker';
import { HTMLPart } from '../HighlightedText';
import {
  StyledHighlightableTextContainer,
  StyledHighlightableTextNotification,
  StyledHighlightableToggle,
} from './HighlightableText.styles';

/**
 *
 * this file make extensive use of React portal, here are some docs: https://react.dev/reference/react-dom/createPortal
 *
 */
export const HighlightableTextWithCitation = ({
  parts,
  onAddCitation,
}: {
  parts: HTMLPart[];
  onAddCitation: (
    payload: { selectedText: string; contextText?: string; startIndex: number; endIndex: number },
    selectionRange: SelectionReference
  ) => void;
}) => {
  const [visibleParts, setVisibleParts] = useState(parts);
  const containerRef = useRef<HTMLSpanElement | null>(null);
  const [selectionUI, setSelectionUI] = useState<SelectionReference | null>(null);
  const [showCitationNotification, setShowCitationNotification] = useState(false);
  const highlightRefs = useRef<Map<number, HTMLElement>>(new Map());
  const [currentHighlightIndex, setCurrentHighlightIndex] = useState<number>(0);

  // Get all indices of parts with tokenReferences
  const highlightIndices = useMemo(
    () => parts.map((part, index) => (part.tokenReference ? index : -1)).filter(idx => idx !== -1),
    [parts]
  );

  useEffect(() => {
    setVisibleParts(parts);

    // Auto-scroll to first token reference
    const firstHighlightIndex = parts.findIndex(part => part.tokenReference);
    if (firstHighlightIndex !== -1) {
      setCurrentHighlightIndex(0);
      // Use setTimeout to ensure DOM is updated
      setTimeout(() => {
        const element = highlightRefs.current.get(firstHighlightIndex);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [parts]);

  const clearSelectionUI = () => setSelectionUI(null);

  // Navigate to a specific highlight
  const scrollAndSetHighlight = (index: number) => {
    if (index < 0 || index >= highlightIndices.length) return;

    const partIndex = highlightIndices[index];
    const element = highlightRefs.current.get(partIndex);

    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setCurrentHighlightIndex(index);
    }
  };

  const goToNextHighlight = () => {
    const nextIndex = (currentHighlightIndex + 1) % highlightIndices.length;
    scrollAndSetHighlight(nextIndex);
  };

  const goToPreviousHighlight = () => {
    const prevIndex = (currentHighlightIndex - 1 + highlightIndices.length) % highlightIndices.length;
    scrollAndSetHighlight(prevIndex);
  };

  // Calculate the character index of a node position within the container
  const getCharacterIndex = (container: Node, node: Node, offset: number): number => {
    const treeWalker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);

    let charCount = 0;
    let currentNode: Node | null;
    // eslint-disable-next-line no-cond-assign
    while ((currentNode = treeWalker.nextNode())) {
      if (currentNode === node) {
        return charCount + offset;
      }
      charCount += currentNode.textContent?.length || 0;
    }

    return charCount;
  };

  const updateSelection = () => {
    const container = containerRef.current;
    if (!container) return;

    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return clearSelectionUI();

    const range = sel.getRangeAt(0);

    // Ignore collapsed selections (caret)
    if (sel.isCollapsed) return clearSelectionUI();

    // Make sure selection is inside our container
    const common = range.commonAncestorContainer;
    const isInside = container.contains(
      common.nodeType === Node.ELEMENT_NODE ? (common as Element) : common.parentElement
    );

    if (!isInside) return clearSelectionUI();

    const text = sel.toString().trim();
    if (!text) return clearSelectionUI();

    const rect = range.getBoundingClientRect();
    const rects = range.getClientRects();

    // If rect is empty (sometimes happens), bail
    if (!rect || (rect.width === 0 && rect.height === 0)) return clearSelectionUI();

    // Calculate character indices
    const startIndex = getCharacterIndex(container, range.startContainer, range.startOffset);
    const endIndex = getCharacterIndex(container, range.endContainer, range.endOffset);

    // Store the range so we can restore it if needed
    const clonedRange = range.cloneRange();

    setSelectionUI({ text, rect, rects, range: clonedRange, startIndex, endIndex });
  };

  // Hide on scroll / resize (position becomes stale)
  useEffect(() => {
    const onScroll = () => clearSelectionUI();
    const onResize = () => clearSelectionUI();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // Hide when clicking outside
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const container = containerRef.current;
      if (!container) return;
      if (!container.contains(e.target as Node)) clearSelectionUI();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // Maintain the selection when the button appears
  useEffect(() => {
    if (selectionUI) {
      // Restore the selection if it was lost
      const sel = window.getSelection();
      if (sel && sel.rangeCount === 0) {
        try {
          sel.addRange(selectionUI.range);
        } catch (e) {
          // Range might be invalid, ignore
        }
      }
    }
  }, [selectionUI]);

  // Auto-hide citation notification after 5 seconds
  useEffect(() => {
    if (showCitationNotification) {
      const timer = setTimeout(() => {
        setShowCitationNotification(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showCitationNotification]);

  const overlay = selectionUI
    ? createPortal(
      <HighlightableTextOverlay
        selectionReference={selectionUI}
        highlightActions={[
          {
            action: params => {
              onAddCitation(params, selectionUI);
              setSelectionUI(null);
              setShowCitationNotification(true);
            },
            label: 'Add Citation',
          },
        ]}
      />,
      document.body
    )
    : null;

  const citationNotification = showCitationNotification
    ? createPortal(
      <StyledHighlightableTextNotification onClick={() => setShowCitationNotification(false)}>
        <span>✓</span>
        <span>Citation toggled. Now click a cell to link it.</span>
      </StyledHighlightableTextNotification>,
      document.body
    )
    : null;

  // Navigation controls for multiple highlights
  const navigationControls =
    highlightIndices.length > 1
      ? createPortal(
        <StyledHighlightableToggle>
          <Button
            onClick={goToPreviousHighlight}
            size="small"
            variant="outlined"
            style={{
              minWidth: '40px',
              padding: '4px',
              fontSize: '18px',
            }}
            title="Previous citation">
              ↑
          </Button>
          <div
            style={{
              textAlign: 'center',
              fontSize: '12px',
              color: '#666',
              padding: '2px 0',
            }}>
            {currentHighlightIndex + 1}/{highlightIndices.length}
          </div>
          <Button
            onClick={goToNextHighlight}
            size="small"
            variant="outlined"
            style={{
              minWidth: '40px',
              padding: '4px',
              fontSize: '18px',
            }}
            title="Next citation">
              ↓
          </Button>
        </StyledHighlightableToggle>,
        document.body
      )
      : null;

  return (
    <>
      <StyledHighlightableTextContainer ref={containerRef} onMouseUp={updateSelection} onKeyUp={updateSelection}>
        {visibleParts.map((part, i) =>
          part.tokenReference ? (
            <HighlightedTextMarker
              htmlPart={part}
              isCurrentHighlight={i === highlightIndices[currentHighlightIndex]}
              key={`highlight-${i}`}
              ref={(el: HTMLElement | null) => {
                if (el) {
                  highlightRefs.current.set(i, el);
                } else {
                  highlightRefs.current.delete(i);
                }
              }}
            />
          ) : (
            <span key={`default-${i}`} dangerouslySetInnerHTML={{ __html: part.text }} />
          )
        )}
      </StyledHighlightableTextContainer>
      {overlay}
      {citationNotification}
      {navigationControls}
    </>
  );
};
